import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Input,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  Icon,
  Link,
  useToast,
  Select,
} from '@chakra-ui/react';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaPaperPlane,
  FaBuilding,
  FaIndustry,
  FaLeaf,
  FaGlobe,
  FaWhatsapp,
} from 'react-icons/fa';

import PageHero from '../Components/PageHero';
import Reveal from '../Components/Reveal';
import contactImage from '../Components/Assets/cacao-arabia.jpg';

const fieldProps = {
  bg: 'white',
  borderColor: 'gray.200',
  borderRadius: 'lg',
  h: '50px',
  fontSize: 'sm',
  transition: 'all 0.2s',
  _hover: { borderColor: 'gray.300' },
  _focusVisible: { borderColor: 'brand.500', boxShadow: '0 0 0 3px rgba(20,138,85,0.18)' },
  _placeholder: { color: 'gray.400' },
};

const labelProps = {
  color: 'gray.700',
  fontWeight: '600',
  fontSize: 'sm',
  mb: 2,
};

function Contact() {
  const toast = useToast();
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    departement: '',
    sujet: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: 'Message envoyé !',
      description: 'Nous vous répondrons dans les plus brefs délais.',
      status: 'success',
      duration: 5000,
      isClosable: true,
      position: 'top',
    });
    setFormData({
      nom: '',
      email: '',
      telephone: '',
      departement: '',
      sujet: '',
      message: '',
    });
  };

  const contactInfo = [
    {
      icon: FaMapMarkerAlt,
      title: 'Siège Social',
      lines: ["Bunia, Province de l'Ituri", 'République Démocratique du Congo'],
      color: 'navy.500',
      bg: 'navy.50',
    },
    {
      icon: FaLeaf,
      title: 'Sites Agro Business',
      lines: ['Tshopo : Territoire de Bafwasende', "Ituri : Territoire d'Irumu (Komanda)"],
      color: 'brand.500',
      bg: 'brand.50',
    },
    {
      icon: FaIndustry,
      title: 'Site Industriel (Clouterie)',
      lines: ["Ituri : Territoire d'Irumu (Village Tsere)"],
      color: 'navy.600',
      bg: 'navy.50',
    },
    {
      icon: FaPhone,
      title: 'Téléphones',
      lines: [
        'Vodacom : +243 836 944 316',
        'Airtel : +243 984 270 966',
        'Orange : +243 844 088 566',
      ],
      color: 'brand.500',
      bg: 'brand.50',
    },
    {
      icon: FaEnvelope,
      title: 'Email',
      lines: ['trimersarl7@gmail.com'],
      color: 'navy.500',
      bg: 'navy.50',
    },
    {
      icon: FaGlobe,
      title: 'Site Web',
      lines: ['www.trimersarl.com'],
      color: 'brand.600',
      bg: 'brand.50',
    },
    {
      icon: FaClock,
      title: "Heures d'Ouverture",
      lines: ['Lundi - Vendredi : 8h00 - 17h00', 'Samedi : 8h00 - 12h00', 'Dimanche : Fermé'],
      color: 'gold.600',
      bg: 'gold.50',
    },
  ];

  return (
    <Box>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <PageHero
        eyebrow="Contactez-nous"
        title="Parlons de"
        accent="votre projet"
        description="Une question ? Un projet ? Notre équipe est à votre écoute. Remplissez le formulaire ci-dessous et nous vous répondrons rapidement."
        image={contactImage}
        icon={FaWhatsapp}
        gradient="linear(120deg, rgba(5,42,27,0.96) 0%, rgba(11,89,58,0.82) 50%, rgba(8,28,52,0.9) 100%)"
        badges={[{ label: 'Réponse < 24h' }, { label: 'Lun - Sam' }]}
      />

      {/* ═══════════════════════ FORM + INFO ═══════════════════════ */}
      <Box py={{ base: 16, md: 24 }} bg="white">
        <Container maxW="7xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 10, lg: 14 }} alignItems="start">
            {/* ─── Form ─── */}
            <Reveal>
              <Box
                bg="gray.50"
                p={{ base: 6, md: 10 }}
                borderRadius="2xl"
                boxShadow="card"
                border="1px solid"
                borderColor="gray.100"
              >
                <VStack spacing={2} align="start" mb={8}>
                  <Heading size="lg" color="gray.900">
                    Envoyez-nous un message
                  </Heading>
                  <Text color="gray.600" fontSize="sm">
                    Tous les champs marqués * sont obligatoires.
                  </Text>
                </VStack>

                <form onSubmit={handleSubmit}>
                  <VStack spacing={5}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                      <FormControl isRequired>
                        <FormLabel {...labelProps}>Nom Complet *</FormLabel>
                        <Input
                          name="nom"
                          value={formData.nom}
                          onChange={handleChange}
                          placeholder="Votre nom"
                          {...fieldProps}
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel {...labelProps}>Email *</FormLabel>
                        <Input
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="votre@email.com"
                          {...fieldProps}
                        />
                      </FormControl>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                      <FormControl>
                        <FormLabel {...labelProps}>Téléphone</FormLabel>
                        <Input
                          name="telephone"
                          value={formData.telephone}
                          onChange={handleChange}
                          placeholder="+243 XXX XXX XXX"
                          {...fieldProps}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel {...labelProps}>Département</FormLabel>
                        <Select
                          name="departement"
                          value={formData.departement}
                          onChange={handleChange}
                          placeholder="Sélectionnez..."
                          {...fieldProps}
                        >
                          <option value="agro">Agro Business (Cacao & Macadamia)</option>
                          <option value="industries">Industries (Clouterie)</option>
                          <option value="partenariat">Partenariat / Investissement</option>
                          <option value="autre">Autre</option>
                        </Select>
                      </FormControl>
                    </SimpleGrid>

                    <FormControl>
                      <FormLabel {...labelProps}>Sujet</FormLabel>
                      <Input
                        name="sujet"
                        value={formData.sujet}
                        onChange={handleChange}
                        placeholder="Sujet de votre message"
                        {...fieldProps}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel {...labelProps}>Message *</FormLabel>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Votre message ici..."
                        rows={6}
                        borderRadius="lg"
                        fontSize="sm"
                        transition="all 0.2s"
                        _hover={{ borderColor: 'gray.300' }}
                        _focusVisible={{ borderColor: 'brand.500', boxShadow: '0 0 0 3px rgba(20,138,85,0.18)' }}
                        _placeholder={{ color: 'gray.400' }}
                      />
                    </FormControl>

                    <Button
                      type="submit"
                      size="lg"
                      rightIcon={<FaPaperPlane />}
                      w="100%"
                      fontWeight="700"
                    >
                      Envoyer le message
                    </Button>

                    <HStack justify="center" spacing={2} pt={1}>
                      <Text fontSize="xs" color="gray.500">
                        Ou écrivez-nous directement sur
                      </Text>
                      <Link
                        href="https://wa.me/243810330830?text=Bonjour%20TRIMER%20SARL%20!"
                        target="_blank"
                        rel="noopener noreferrer"
                        fontSize="xs"
                        fontWeight="700"
                        color="brand.600"
                        display="inline-flex"
                        alignItems="center"
                        gap={1}
                        _hover={{ color: 'brand.800' }}
                      >
                        <Icon as={FaWhatsapp} />
                        WhatsApp
                      </Link>
                    </HStack>
                  </VStack>
                </form>
              </Box>
            </Reveal>

            {/* ─── Info ─── */}
            <VStack spacing={4} align="stretch">
              {contactInfo.map((info, index) => (
                <Reveal key={info.title} delay={index * 0.05}>
                  <HStack
                    spacing={5}
                    p={5}
                    bg="white"
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="gray.100"
                    boxShadow="soft"
                    borderLeft="4px solid"
                    borderLeftColor={info.color}
                    align="start"
                    transition="all 0.3s"
                    _hover={{ boxShadow: 'card', transform: 'translateX(4px)' }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      h="44px"
                      w="44px"
                      borderRadius="lg"
                      bg={info.bg}
                      flexShrink={0}
                    >
                      <Icon as={info.icon} boxSize={5} color={info.color} />
                    </Box>
                    <Box>
                      <Text fontWeight="700" color="gray.900" mb={1} fontSize="sm">
                        {info.title}
                      </Text>
                      {info.lines.map((line, i) => (
                        <Text key={i} color="gray.600" fontSize="xs" lineHeight="1.7">
                          {info.href ? (
                            <Link
                              href={info.href}
                              _hover={{ color: info.color, textDecoration: 'underline' }}
                              target={info.href.startsWith('http') ? '_blank' : undefined}
                              rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                            >
                              {line}
                            </Link>
                          ) : (
                            line
                          )}
                        </Text>
                      ))}
                    </Box>
                  </HStack>
                </Reveal>
              ))}

              {/* Map card */}
              <Reveal delay={0.1}>
                <Box
                  borderRadius="2xl"
                  height="200px"
                  position="relative"
                  overflow="hidden"
                  boxShadow="card"
                >
                  <Box
                    position="absolute"
                    inset={0}
                    bgImage={`url(${contactImage})`}
                    bgSize="cover"
                    bgPosition="center"
                  />
                  <Box position="absolute" inset={0} bgGradient="linear(to-t, rgba(8,28,52,0.92), rgba(8,28,52,0.35))" />
                  <VStack color="white" position="relative" zIndex={1} h="100%" justify="center" spacing={1}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      h="52px"
                      w="52px"
                      borderRadius="full"
                      bg="whiteAlpha.200"
                      backdropFilter="blur(8px)"
                      border="1px solid"
                      borderColor="whiteAlpha.300"
                      mb={2}
                    >
                      <Icon as={FaMapMarkerAlt} boxSize={6} />
                    </Box>
                    <Text fontWeight="800" fontSize="sm">
                      TRIMER SARL
                    </Text>
                    <Text fontSize="xs" color="whiteAlpha.800">
                      Bunia, Province de l'Ituri, RDC
                    </Text>
                  </VStack>
                </Box>
              </Reveal>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* ═══════════════════════ QUICK CONTACT BAR ═══════════════════════ */}
      <Box py={{ base: 14, md: 16 }} color="white" position="relative" overflow="hidden" bg="navy.900">
        <Box position="absolute" inset={0} className="bg-grid" opacity={0.35} />
        <Box
          position="absolute"
          top="-120px"
          left="50%"
          transform="translateX(-50%)"
          w="600px"
          h="300px"
          borderRadius="full"
          bgGradient="radial(circle at center, rgba(20,138,85,0.28), transparent 70%)"
        />

        <Container maxW="7xl" position="relative">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} textAlign="center">
            {[
              { icon: FaPhone, title: 'Appelez-nous', body: '+243 836 944 316', href: 'tel:+243836944316' },
              { icon: FaEnvelope, title: 'Écrivez-nous', body: 'trimersarl7@gmail.com', href: 'mailto:trimersarl7@gmail.com' },
              { icon: FaBuilding, title: 'Visitez-nous', body: "Bunia, Province de l'Ituri, RDC" },
            ].map((item, index) => (
              <Reveal key={item.title} delay={index * 0.08}>
                <VStack spacing={3}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    h="56px"
                    w="56px"
                    borderRadius="xl"
                    bg="whiteAlpha.200"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                  >
                    <Icon as={item.icon} boxSize={6} color="gold.300" />
                  </Box>
                  <Text fontWeight="700">{item.title}</Text>
                  {item.href ? (
                    <Link href={item.href} fontSize="sm" color="whiteAlpha.800" _hover={{ color: 'white' }}>
                      {item.body}
                    </Link>
                  ) : (
                    <Text fontSize="sm" color="whiteAlpha.800">
                      {item.body}
                    </Text>
                  )}
                </VStack>
              </Reveal>
            ))}
          </SimpleGrid>
        </Container>
      </Box>
    </Box>
  );
}

export default Contact;
